import { useQuery } from "@tanstack/react-query";
import {
  fetchOpenTrades,
  fetchPaperAccounts,
  fetchRecentTrades,
} from "../../lib/api.js";
import { TradesTable } from "./TradesTable.js";

export function PaperDashboard() {
  const accounts = useQuery({
    queryKey: ["paper-accounts"],
    queryFn: fetchPaperAccounts,
    refetchInterval: 5_000,
  });
  const open = useQuery({
    queryKey: ["paper-trades-open"],
    queryFn: () => fetchOpenTrades("PAPER"),
    refetchInterval: 5_000,
  });
  const recent = useQuery({
    queryKey: ["paper-trades-recent"],
    queryFn: () => fetchRecentTrades("PAPER", 100),
    refetchInterval: 10_000,
  });

  const rows = (accounts.data ?? []).slice().sort((a, b) => b.equity - a.equity);

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto bg-zinc-950 p-4">
      <section>
        <h2 className="mb-2 text-xs uppercase tracking-wider text-zinc-400">
          Per-strategy paper accounts
        </h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {rows.map((a) => {
            const pnlPct =
              a.startingBalance > 0
                ? ((a.equity - a.startingBalance) / a.startingBalance) * 100
                : 0;
            const pnlColor = pnlPct >= 0 ? "text-accent" : "text-bear";
            return (
              <div
                key={a.id}
                className="rounded border border-zinc-800 bg-zinc-900/40 p-3 text-xs"
              >
                <div className="font-mono text-zinc-300">{a.strategyId}</div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-zinc-500">Equity</span>
                  <span className="font-mono">${a.equity.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">Realized</span>
                  <span className={`font-mono ${pnlColor}`}>
                    ${a.realizedPnl.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">Return</span>
                  <span className={`font-mono ${pnlColor}`}>
                    {pnlPct.toFixed(2)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded border border-zinc-800">
        <header className="border-b border-zinc-800 px-3 py-2 text-xs uppercase tracking-wider text-zinc-400">
          Open paper positions
        </header>
        <TradesTable
          trades={open.data ?? []}
          emptyMessage="No open paper positions."
        />
      </section>

      <section className="rounded border border-zinc-800">
        <header className="border-b border-zinc-800 px-3 py-2 text-xs uppercase tracking-wider text-zinc-400">
          Recent paper trades
        </header>
        <TradesTable
          trades={recent.data ?? []}
          emptyMessage="No closed paper trades yet — waiting for SL/TP fills."
        />
      </section>
    </div>
  );
}
